/**
 * @module Root
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/*tslint:disable*/
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { bootloader } from '@angularclass/hmr';

import { enableProdMode } from '@angular/core';
import { Md5 } from 'ts-md5/dist/md5';
import {randomNumber as cryptoRandomNumber} from '@support/shared/services/random-number.helper';

/*
 * App Module
 * our top level module that holds all of our components
 */
import { WebModule } from '@root/web/web.module';
import { ConfigLoader } from '@root/config-loader';
import { environment } from '@environment/environment';
import { SETTINGS } from '@environment/settings';
import { WEB_API_MAP } from '@core/api/proxies/web-api-endpoints';
if (environment.production) {
  enableProdMode();
  // Put to disable all console logs
  if (window && window.console) {
    window.console.log = () => {};
  }
}
import 'zone.js';

const configLoader = new ConfigLoader();

/*
 * Bootstrap our Angular app with a top level NgModule
 * Web channel variant bootstrap for JIT compilation
 */
export const webJIT = (): Promise<any> => {
  return platformBrowserDynamic()
    .bootstrapModule(WebModule)
    .catch((err: any) => {
      console.error(err);
    });
};

const getFeatureEnabled = (featureName: string): boolean => {
  const config = configLoader.getConfig('featureToggleConfig');
  if (config && config.features && config.features[featureName]) {
    return !!config.features[featureName].enabled;
  }
  return false;
}

const checkLoginTo = ():void => {
  const queryParams = new URLSearchParams(window.location.search);
  const loginToKey = 'login_to';
  const codeKey = 'code';
  const loginToValue = queryParams.get(loginToKey);
  const preSetLoginToValue = localStorage.getItem(loginToKey);
  const code = queryParams.get(codeKey);
  if (preSetLoginToValue && code) {
    sessionStorage.setItem('state', preSetLoginToValue);
    localStorage.removeItem(loginToKey);
  } else if (loginToValue) {
    localStorage.setItem(loginToKey, loginToValue);
    window.location.href = configLoader.getConfig('eServiceRoot');
  }
};
/**
 * Retrieves an anonymous token for authentication.
 * This function handles two scenarios:
 * 1. TAG Proxy: A new implementation replacing Apigee, using basic auth.
 * 2. Legacy: The original implementation using POP (Proof of Possession) tokens.
 * The TAG Proxy is enabled/disabled via a feature flag.
 */
export const getAnonymousToken = (): void => {
  const TAG_PROXY_ENABLED = getFeatureEnabled('FEATURE.TAG_PROXY');
  const PHOENIX_BYOD_ENABLED = getFeatureEnabled('FEATURE.PHOENIX_BYOD');
  const CLIENT_ID = TAG_PROXY_ENABLED 
    ? configLoader.getConfig('TAGClientId')
    : configLoader.getConfig('clientId') || SETTINGS.CLIENT_ID;
  const GRANT_TYPE = configLoader.getConfig('grantType');

  // Skip token request for home route
  const route = window.location.href.replace(window.location.origin, '').replace(/[^a-zA-Z0-9 ]/g, '');
  if (!route.length || route === 'home') {
    triggerWebJIT();
    return;
  }

  const requestEndPoint = TAG_PROXY_ENABLED 
    ? WEB_API_MAP.AUTH_TOKEN_API_V1 
    : WEB_API_MAP.ANON_TOKEN_API_V6;

  //const url = `${configLoader.getConfig(requestEndPoint.domain)}${requestEndPoint.url}`;
  const url = formatURL(requestEndPoint,TAG_PROXY_ENABLED);
  // checking if token already exists , if so not making additional call
  if (PHOENIX_BYOD_ENABLED) {
    const phx_ng_token = sessionStorage.getItem('phx_ng_token');
    if (phx_ng_token) {
      localStorage.setItem('webanontoken', phx_ng_token);
      triggerWebJIT();
      return;
    } else{
      sendTokenRequest(url, CLIENT_ID);
    }
  } else if (TAG_PROXY_ENABLED) {
    sendTokenRequest(url, CLIENT_ID);
  } else {
    sendLegacyTokenRequest(url, CLIENT_ID, GRANT_TYPE);
  }
};
//Format URL from RequestEndPoint Config Object
function formatURL(endpointConfig, TAG_PROXY= false):string {
  let baseUrl = configLoader.getConfig(endpointConfig.domain);
    
  let url: string;
  if (endpointConfig.is_tag_proxy || TAG_PROXY) {
    const proxyNameRegex = /{{PROXY_NAME}}/g;
    baseUrl = baseUrl.replace(proxyNameRegex, endpointConfig.proxyName);
    url = baseUrl + endpointConfig.url;
  } else {
    url = endpointConfig.proxyName 
      ? baseUrl + endpointConfig.proxyName + endpointConfig.url
      : baseUrl + endpointConfig.url;
  }
  return url;
}

function getRandom(max: number): number {
  const base: number = 10;
  return Math.floor(cryptoRandomNumber() * Math.pow(base, max));
}

function trimId(id: string, limit: number): string {
  return (id.length > limit) ? id.substring(0, limit) : id;
}

/**
   * Add activity id to header using customer id / random number
   */
function getActivityId(): string {
  const limit: number = 32;
  const randomNumber: number = 20;
  const timestamp = new Date().getTime();
  const generatedId: string = ('').concat(timestamp.toString(), getRandom(randomNumber).toString(),
  getRandom(randomNumber).toString());
  const activityId: string = trimId(generatedId, limit);
  return activityId;
}

function generateInteractionId(partialToken: string): string {
  const limit: number = 49;
  const limitForDate: number = 5;
  // tslint:disable-next-line:no-magic-numbers
  let dateForInteractionId = Math.floor(new Date().getTime() / 1000).toString();

  dateForInteractionId = dateForInteractionId = dateForInteractionId.split('').reverse().join('');
  const channel = "WEB";
  const sessionId = Md5.hashStr(partialToken);
  const dynamicSessionSubString = 'ANON';
  const dynamicSessionID = ('').concat(dynamicSessionSubString,
    trimId(dateForInteractionId.toString(), limitForDate));
    // checking if token is available in cookie for cart persistance ,
    // if found then using it ,else creating new token
    let interactionIdFromCookie;
    let match = document.cookie.match(new RegExp(`${'interactionId'}=([^;]+)`));
    if(match){
      interactionIdFromCookie = match[1];
    }
    let interactionId;
    if(!interactionIdFromCookie){
      interactionId = trimId((channel).concat('',
        sessionId.toString(), dynamicSessionID), limit);
        sessionStorage.setItem('interactionId', interactionId);
    }else{
      interactionId = interactionIdFromCookie;
    }
    sessionStorage.setItem('interactionId', interactionId);
  return interactionId;
}

function sendTokenRequest(url: string, clientId: string): void {
  const xhr = new XMLHttpRequest();

  const requestPOP = { 
    jwk:""
  };
  const activityId = getActivityId();
  const partialToken = configLoader.generatePartialToken(
    url,
    JSON.stringify(requestPOP),
    'POST',
    configLoader.getConfig('kid'),
    activityId
  );
  xhr.open('POST', url, true);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('activityId', activityId);
  // if interactionId is already gernerated, use it from sessionStorage
  // else generate a new one and store it in sessionStorage
  // this is to ensure that interactionId is consistent across the Phoenix and Angular applications
  const interactionId = sessionStorage.getItem('interactionId') 
    ? sessionStorage.getItem('interactionId') : generateInteractionId(partialToken);
  xhr.setRequestHeader('channelId', 'WEB');
  xhr.setRequestHeader('applicationId', 'REBELLION');
  xhr.setRequestHeader('interactionId', interactionId);
  
  xhr.onload = () => {
    if (xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      localStorage.setItem('webanontoken', response.access_token);
      sessionStorage.setItem('phx_ng_token', response.access_token); // setting anon-token to persist b/w phoenix and ng
      triggerWebJIT();
    } else {
      console.error('Token request failed');
    }
  };

  configLoader.generatePopToken(partialToken).pipe(
    map((signature: ArrayBuffer) => `${partialToken}.${Base64URL.stringify(new Uint8Array(signature))}`)
  ).subscribe((token: string) => {
    xhr.setRequestHeader('x-authorization', token);
    const payload = {
      jwk:""
    };
    xhr.send(JSON.stringify(payload));
  });
}

function sendLegacyTokenRequest(url: string, clientId: string, grantType: string): void {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', url, true);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('channelId', 'WEB');
  xhr.setRequestHeader('applicationId', 'REBELLION');
  xhr.setRequestHeader('senderId', 'TMO');
  const activityId = new Date().getTime().toString();
  xhr.setRequestHeader('activityId', activityId);

  xhr.onload = () => {
    if (xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      localStorage.setItem('webanontoken', response.access_token);
      triggerWebJIT();
    } else {
      console.error('Legacy token request failed');
    }
  };

  const requestPOP = { 
    client_id: clientId, 
    cnf: self['pKey'] 
  };
  
  const partialToken = configLoader.generatePartialToken(
    url,
    JSON.stringify(requestPOP),
    'POST',
    configLoader.getConfig('kid'),
    activityId
  );

  configLoader.generatePopToken(partialToken).pipe(
    map((signature: ArrayBuffer) => `${partialToken}.${Base64URL.stringify(new Uint8Array(signature))}`)
  ).subscribe((token: string) => {
    xhr.setRequestHeader('x-authorization', token);
    const payload = {
      client_id: clientId,
      cnf: self['pKey']
    };
    xhr.send(JSON.stringify(payload));
  });
}
// needed for hmr
// in prod this is replace for document ready
ConfigLoader.load()
  .then(() => {
    checkLoginTo();
    getAnonymousToken();
  }, (err: any) => {
    console.error('error in loading config:', err);
  })
  .catch((err: any) => {
    console.error('error in loading config', err);
  });

export const triggerWebJIT = (): void => {
  if (environment.hmr) {
    bootloader(webJIT);
  } else {
    webJIT();
  }
};

/***************************************************************************************************
* Zone JS is required by default for Angular itself.
* Must import afer main module is imported/loads because Promise is overwritten
* by Sub module 'reb-webworker' promise-worker library.
*/
import 'zone.js'; // Included with Angular CLI.import { environment } from '@environment/environment';import { map } from 'rxjs/operators';
import { Base64URL } from '@support/shared/services/encoding-utils';
import { map } from 'rxjs/operators';
